import React, { createContext, useContext, useState, ReactNode } from 'react';

export type UserRole = 'voter' | 'journalist' | 'analyst';

interface UserRoleContextType {
  role: UserRole;
  setRole: (role: UserRole) => void;
  isAtLeast: (minRole: UserRole) => boolean;
  hasFeature: (feature: RoleFeature) => boolean;
}

// Feature flags per role — ordered: voter < journalist < analyst
export type RoleFeature =
  | 'candidates'
  | 'compare'
  | 'manifestos'
  | 'vote'
  | 'elections'
  | 'live_results'
  | 'countdown'
  | 'insights'
  | 'social_media'
  | 'data_insights'
  | 'data_export'
  | 'track_record'
  | 'public_opinion'
  | 'fact_network'
  | 'career_intelligence'
  | 'candidate_network'
  | 'audit_trail';

const ROLE_FEATURES: Record<UserRole, Set<RoleFeature>> = {
  voter: new Set([
    'candidates', 'compare', 'manifestos', 'vote',
    'elections', 'live_results', 'countdown',
  ]),
  journalist: new Set([
    'candidates', 'compare', 'manifestos',
    'elections', 'live_results', 'countdown',
    'insights', 'social_media', 'data_insights',
    'data_export', 'track_record', 'public_opinion',
  ]),
  analyst: new Set([
    'candidates', 'compare', 'manifestos',
    'elections', 'live_results', 'countdown',
    'insights', 'social_media', 'data_insights',
    'data_export', 'track_record', 'public_opinion',
    'fact_network', 'career_intelligence', 'candidate_network', 'audit_trail',
  ]),
};

const ROLE_ORDER: UserRole[] = ['voter', 'journalist', 'analyst'];

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined);

const STORAGE_KEY = 'electure_user_role';

export const UserRoleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [role, setRoleState] = useState<UserRole>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'voter' || saved === 'journalist' || saved === 'analyst') return saved;
    return 'voter';
  });

  const setRole = (newRole: UserRole) => {
    localStorage.setItem(STORAGE_KEY, newRole);
    setRoleState(newRole);
  };

  const isAtLeast = (minRole: UserRole) =>
    ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf(minRole);

  const hasFeature = (feature: RoleFeature) =>
    ROLE_FEATURES[role].has(feature);

  return (
    <UserRoleContext.Provider value={{ role, setRole, isAtLeast, hasFeature }}>
      {children}
    </UserRoleContext.Provider>
  );
};

export const useUserRole = (): UserRoleContextType => {
  const ctx = useContext(UserRoleContext);
  if (!ctx) throw new Error('useUserRole must be used within UserRoleProvider');
  return ctx;
};
